import React, { useState, useEffect } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import '../styles/contact.css';

const Contact = () => {
    const { user, isAuthenticated } = useFirebase();
    const [formData, setFormData] = useState({
        subject: '',
        message: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [userInfo, setUserInfo] = useState({
        username: '',
        email: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    // Load user information from profile
    useEffect(() => {
        const fetchUserInfo = async () => {
            if (!isAuthenticated) return;
            
            try {
                const response = await apiClient.get('/api/profile');
                
                let profileData;
                if (response instanceof Response) {
                    if (response.ok) {
                        profileData = await response.json();
                    } else {
                        logger.warn(`Failed to fetch profile for contact page: ${response.status}`);
                        profileData = null;
                    }
                } else {
                    profileData = response;
                }
                
                if (profileData) {
                    setUserInfo({
                        username: profileData.username || '',
                        email: profileData.email || user?.email || ''
                    });
                } else {
                    setUserInfo({
                        username: localStorage.getItem('username') || user?.displayName || '',
                        email: user?.email || ''
                    });
                }
            } catch (error) {
                logger.error('Error fetching user info:', error);
                setUserInfo({
                    username: localStorage.getItem('username') || user?.displayName || '',
                    email: user?.email || ''
                });
            }
        };
        
        fetchUserInfo();
    }, [isAuthenticated, user]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Validate input
        let error = '';
        if (name === 'subject' && value.length > 100) {
            error = 'Subject cannot exceed 100 characters';
        } else if (name === 'message' && value.length > 2000) {
            error = 'Message cannot exceed 2000 characters';
        }
        
        // Update form errors
        setFormErrors({
            ...formErrors,
            [name]: error
        });
        
        // Update form values
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Submit the contact form
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Clear previous status
        setSubmitStatus(null);
        
        // Validate form before submission
        const newErrors = {};
        
        if (!formData.subject.trim()) {
            newErrors.subject = 'Subject is required';
        } else if (formData.subject.length > 100) {
            newErrors.subject = 'Subject cannot exceed 100 characters';
        }
        
        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        } else if (formData.message.length > 2000) {
            newErrors.message = 'Message cannot exceed 2000 characters';
        }
        
        // If there are errors, update state and stop submission
        if (Object.keys(newErrors).length > 0) {
            setFormErrors(newErrors);
            return;
        }
        
        setLoading(true);
        
        try {
            const response = await apiClient.post('/api/contact', {
                subject: formData.subject,
                message: formData.message
            });
            
            let responseData;
            
            if (response instanceof Response) {
                if (!response.ok) {
                    try {
                        const contentType = response.headers.get('content-type');
                        if (contentType && contentType.includes('application/json')) {
                            const errorJson = await response.json();
                            throw new Error(errorJson.message || 'Failed to send message');
                        } else {
                            const errorText = await response.text();
                            throw new Error(errorText || 'Failed to send message');
                        }
                    } catch (parseError) {
                        throw new Error(`Failed to send message (${response.status})`);
                    }
                }
                
                try {
                    responseData = await response.json();
                } catch (jsonError) {
                    responseData = { message: 'Your message has been sent successfully!' };
                }
            } else {
                responseData = response;
            }
            
            // Set success status
            setSubmitStatus({
                type: 'success',
                message: responseData.message || 'Your message has been sent successfully!'
            });
            
            // Clear the form
            setFormData({
                subject: '',
                message: ''
            });
        } catch (error) {
            logger.error('Error sending contact message:', error);
            
            // Set error status
            setSubmitStatus({
                type: 'error',
                message: error.message || 'Failed to send message. Please try again later.'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="contact-container">
                <h1 className="page-title">Contact Us</h1>
                <div className="contact-content">
                    <div className="contact-form-container">
                        <p>Please sign in to contact us.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="contact-container">
            <h1 className="page-title">Contact Us</h1>
            
            <div className="contact-content">
                <p className="contact-intro">
                    Have a question, suggestion, or just want to say hello? We'd love to hear from you!
                    Fill out the form below and we'll get back to you as soon as possible.
                </p>
                
                <div className="contact-form-container">
                    <div className="contact-user-info">
                        <p><strong>From:</strong> {userInfo.username}</p>
                        <p><strong>Email:</strong> {userInfo.email}</p>
                    </div>
                    
                    <form className="contact-form" onSubmit={handleSubmit}>
                        <div className="contact-form-group">
                            <label htmlFor="subject">Subject:</label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                className={`contact-input ${formErrors.subject ? 'input-error' : ''}`}
                                placeholder="What's your message about?"
                                disabled={loading}
                            />
                            <div className="contact-field-info">
                                {formErrors.subject && (
                                    <span className="contact-error-text">{formErrors.subject}</span>
                                )}
                                <span className="contact-char-count">
                                    {formData.subject.length}/100
                                </span>
                            </div>
                        </div>
                        
                        <div className="contact-form-group">
                            <label htmlFor="message">Message:</label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                className={`contact-textarea ${formErrors.message ? 'input-error' : ''}`}
                                placeholder="Type your message here..."
                                rows={8}
                                disabled={loading}
                            ></textarea>
                            <div className="contact-field-info">
                                {formErrors.message && (
                                    <span className="contact-error-text">{formErrors.message}</span>
                                )}
                                <span className="contact-char-count">
                                    {formData.message.length}/2000
                                </span>
                            </div>
                        </div>
                        
                        <button 
                            type="submit" 
                            className="contact-submit-button"
                            disabled={loading}
                        >
                            {loading ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                    
                    {submitStatus && submitStatus.type === 'success' && (
                        <div className="contact-success-message">
                            {submitStatus.message}
                        </div>
                    )}
                    
                    {submitStatus && submitStatus.type === 'error' && (
                        <div className="contact-error-message">
                            {submitStatus.message}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Contact; 